import {
   Section,
   StructuredAnswer,
   StructuredQuestion,
   StructuredSurvey,
   StructuredSurveyTemplate,
} from '@dreryk/egabinet-survey-frontend-api';

import { readInitialShowOnVisit } from '../seam/showOnVisitSeam';

import {
   DetailQuestionViewModel,
   FieldScope,
   GateOption,
   GateOptionKind,
   MedicalProfileAnswerValue,
   MedicalProfileTabViewModel,
   SectionNodeViewModel,
   SectionRowViewModel,
} from './MedicalProfileTab.types';
import { isGateQuestion, isVisibilityControlQuestion } from './MedicalProfileVisibility.utils';

const GATE_OPTION_CODES: Record<GateOptionKind, string> = {
   YES: 'YES',
   NO: 'NO',
   UNKNOWN: 'UNKNOWN',
   REFUSED: 'REFUSED',
};

export const GATE_OPTION_KINDS: GateOptionKind[] = ['YES', 'NO', 'UNKNOWN', 'REFUSED'];

export const resolveGateOptionKind = (code: string): GateOptionKind | null => {
   const kind = GATE_OPTION_KINDS.find((candidate) => GATE_OPTION_CODES[candidate] === code);
   return kind ?? null;
};

export const createFormFieldName = (templateCode: string, questionId: number, scope?: FieldScope): string =>
   scope?.entry
      ? `${templateCode}:entry:${scope.entry.sectionId}:${scope.entry.key}:${questionId}`
      : `${templateCode}:${questionId}`;

export const createEntryKeysFieldName = (templateCode: string, sectionId: number): string =>
   `${templateCode}:entries:${sectionId}`;

export const createShowOnVisitFieldName = (templateCode: string, sectionId: number): string =>
   `${templateCode}:section:${sectionId}:showOnVisit`;

const collectNodeQuestionIds = (node: SectionNodeViewModel): number[] => [
   ...node.questions.map((question) => question.questionId),
   ...node.childSections.flatMap(collectNodeQuestionIds),
];

export const createEntryFieldNames = (templateCode: string, node: SectionNodeViewModel, entryKey: string): string[] =>
   collectNodeQuestionIds(node).map((questionId) =>
      createFormFieldName(templateCode, questionId, { entry: { sectionId: node.sectionId, key: entryKey } })
   );

const mapGateOptions = (gateQuestion: StructuredQuestion): GateOption[] => {
   const options = gateQuestion.config?.options ?? [];
   return GATE_OPTION_KINDS.reduce<GateOption[]>((gateOptions, kind) => {
      const option = options.find((candidate) => resolveGateOptionKind(candidate.code) === kind);
      if (option) {
         gateOptions.push({ kind: kind, code: option.code });
      }
      return gateOptions;
   }, []);
};

const findAnswer = (answers: StructuredAnswer[], questionId: number): StructuredAnswer | undefined =>
   answers.find((answer) => answer.questionId === questionId);

export const getSingleSelectionCode = (answer: StructuredAnswer | undefined): string | undefined =>
   answer?.selections?.[0]?.code;

export const answerToCodes = (answer: StructuredAnswer | undefined): string[] =>
   (answer?.selections ?? []).map((selection) => selection.code);

export const valueToCodes = (value: MedicalProfileAnswerValue | undefined): string[] => {
   if (typeof value === 'string') {
      return [value];
   }
   return Array.isArray(value) ? value : [];
};

const hasParentQuestion = (section: Section): boolean =>
   section.parentQuestionId !== undefined && section.parentQuestionId !== null;

const compareSections = (first: Section, second: Section): number =>
   first.displayOrder - second.displayOrder || first.id - second.id;

const mapQuestion = (question: StructuredQuestion, answers: StructuredAnswer[]): DetailQuestionViewModel => ({
   questionId: question.id,
   answerType: question.answerType,
   content: question.content,
   required: question.required,
   config: question.config,
   existingAnswer: findAnswer(answers, question.id),
});

const findChildSections = (template: StructuredSurveyTemplate, parentQuestionIds: number[]): Section[] =>
   template.sections
      .filter(
         (section) =>
            section.parentQuestionId !== undefined &&
            section.parentQuestionId !== null &&
            parentQuestionIds.includes(section.parentQuestionId)
      )
      .sort(compareSections);

const mapSectionNode = (
   section: Section,
   template: StructuredSurveyTemplate,
   answers: StructuredAnswer[],
   visitedSectionIds: number[]
): SectionNodeViewModel => {
   const questions = template.questions
      .filter((question) => question.sectionId === section.id)
      .sort((first, second) => first.questionNumber - second.questionNumber);
   const nextVisited = [...visitedSectionIds, section.id];
   return {
      sectionId: section.id,
      title: section.title,
      repeatable: section.repeatable,
      questions: questions.map((question) => mapQuestion(question, answers)),
      childSections: findChildSections(
         template,
         questions.map((question) => question.id)
      )
         .filter((child) => !nextVisited.includes(child.id))
         .map((child) => mapSectionNode(child, template, answers, nextVisited)),
   };
};

const mapDetailNodes = (
   template: StructuredSurveyTemplate,
   gateQuestion: StructuredQuestion,
   answers: StructuredAnswer[],
   rootSectionId: number
): SectionNodeViewModel[] =>
   findChildSections(template, [gateQuestion.id])
      .filter((section) => section.id !== rootSectionId)
      .map((section) => mapSectionNode(section, template, answers, [rootSectionId]));

export const collectNodeControlQuestionIds = (
   template: StructuredSurveyTemplate,
   node: SectionNodeViewModel,
   includeOwnQuestions: boolean
): number[] => {
   const templateQuestions = new Map(template.questions.map((question) => [question.id, question]));
   const ownIds = includeOwnQuestions
      ? node.questions
           .filter((question) => {
              const templateQuestion = templateQuestions.get(question.questionId);
              return templateQuestion !== undefined && isVisibilityControlQuestion(template, templateQuestion);
           })
           .map((question) => question.questionId)
      : [];
   const childIds = node.childSections
      .filter((child) => !child.repeatable)
      .flatMap((child) => collectNodeControlQuestionIds(template, child, true));
   return [...ownIds, ...childIds];
};

export const collectRowControlQuestionIds = (
   template: StructuredSurveyTemplate,
   row: SectionRowViewModel
): number[] => [
   row.gateQuestionId,
   ...row.details
      .filter((node) => !node.repeatable)
      .flatMap((node) => collectNodeControlQuestionIds(template, node, true)),
];

const mapSectionRow = (
   section: Section,
   gateQuestion: StructuredQuestion,
   template: StructuredSurveyTemplate,
   answers: StructuredAnswer[],
   structuredSurvey: StructuredSurvey | null
): SectionRowViewModel => {
   const gateAnswer = findAnswer(answers, gateQuestion.id);

   return {
      sectionId: section.id,
      title: section.title ?? gateQuestion.content,
      displayOrder: section.displayOrder,
      gateQuestionId: gateQuestion.id,
      gateOptions: mapGateOptions(gateQuestion),
      details: mapDetailNodes(template, gateQuestion, answers, section.id),
      initialGateCode: getSingleSelectionCode(gateAnswer),
      hasData: gateAnswer !== undefined,
      lastUpdateDate: undefined,
      initialShowOnVisit: readInitialShowOnVisit(structuredSurvey, section.id),
   };
};

export const mapTabViewModel = (
   templateCode: string,
   template: StructuredSurveyTemplate,
   structuredSurvey: StructuredSurvey | null
): MedicalProfileTabViewModel => {
   const answers = structuredSurvey?.answers ?? [];
   const rows = template.sections
      .filter((section) => !hasParentQuestion(section))
      .sort(compareSections)
      .reduce<SectionRowViewModel[]>((sectionRows, section) => {
         const gateQuestion = template.questions.find(
            (question) => question.sectionId === section.id && isGateQuestion(question)
         );
         if (gateQuestion) {
            sectionRows.push(mapSectionRow(section, gateQuestion, template, answers, structuredSurvey));
         }
         return sectionRows;
      }, []);

   return {
      templateCode: templateCode,
      surveyId: structuredSurvey?.id ?? null,
      template: template,
      initialAnswers: answers,
      rows: rows,
   };
};
